import { notification } from 'antd';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../../../store/hooks';
import type { AxiosBaseQueryError } from '../../../api/axiosBaseQuery';
import {
  useRefreshAndRecalculateVariantDataMutation,
  variantApi,
} from '../../../api/variantApi';
import { selectUsername } from '../../../authentication/redux/selectors';
import {
  REFRESH_PENDING_TASK_ID_PREFIX,
  RUNNING_TASK_TYPES,
} from '../../../running_tasks/constants';
import { NOTIFICATION_MESSAGES } from '../../constants';
import { useHasRunningRefresh } from '../../hooks/useHasRunningRefresh';
import { getInterpretationSnapshotCapture } from '../../interpretation/snapshotCapture';
import type {
  InterpretationSnapshotCreateRequest,
  RunningTask,
} from '../../redux';

export const useRefreshVariantLogic = (variantId?: string | number) => {
  const dispatch = useAppDispatch();
  const username = useSelector(selectUsername);
  const [isPreparingSnapshot, setIsPreparingSnapshot] = useState(false);
  const [refreshVariantData, { isLoading }] =
    useRefreshAndRecalculateVariantDataMutation();

  const hasRunningRefresh = useHasRunningRefresh(variantId);

  const removePendingTask = useCallback(
    (pendingTaskId: string) => {
      dispatch(
        variantApi.util.updateQueryData(
          'getRunningTasks',
          undefined,
          (tasks) => {
            const pendingTaskIndex = tasks.findIndex(
              (task) => task.task_id === pendingTaskId,
            );
            if (pendingTaskIndex >= 0) {
              tasks.splice(pendingTaskIndex, 1);
            }
          },
        ),
      );
    },
    [dispatch],
  );

  const handleRefresh = useCallback(async () => {
    if (!variantId) {
      return;
    }

    if (hasRunningRefresh) {
      notification.info({
        message: NOTIFICATION_MESSAGES.REFRESH_ALREADY_RUNNING,
      });
      return;
    }

    const snapshotCapture = getInterpretationSnapshotCapture();
    const shouldCaptureSnapshot = snapshotCapture?.shouldCapture() ?? false;
    const numericVariantId = Number(variantId);

    // Capture the interpretation snapshot HTML *before* dispatching the pending
    // task. The hidden InterpretationSnapshotCapture host (and interpretationRef)
    // is only mounted while the variant-workflow route is active, so we must
    // capture while the DOM is still present.
    setIsPreparingSnapshot(true);
    let preparedSnapshot: InterpretationSnapshotCreateRequest | null = null;
    try {
      if (shouldCaptureSnapshot && snapshotCapture) {
        preparedSnapshot = await snapshotCapture.prepare();
      }
    } catch {
      setIsPreparingSnapshot(false);
      notification.error({
        message: NOTIFICATION_MESSAGES.ERROR,
        description: NOTIFICATION_MESSAGES.SNAPSHOT_PREPARATION_FAILED,
      });
      return;
    }
    setIsPreparingSnapshot(false);

    const pendingTaskId = `${REFRESH_PENDING_TASK_ID_PREFIX}${numericVariantId}-${Date.now()}`;
    const pendingTask: RunningTask = {
      task_id: pendingTaskId,
      is_optimistic: true,
      task_type: RUNNING_TASK_TYPES.REFRESH_AND_RECALCULATE,
      status: 'running',
      username: username ?? '',
      variant_id: numericVariantId,
      variant_hgvs: null,
      description: '',
      error_message: null,
      created: new Date().toISOString(),
      completed_at: null,
    };

    dispatch(
      variantApi.util.updateQueryData('getRunningTasks', undefined, (tasks) => {
        tasks.unshift(pendingTask);
      }),
    );

    // Fire-and-forget: the task runs in the background while the user stays
    // on the variant page. The running-task indicator (analogous to the
    // variant-lock banner) disables add/edit buttons until the task completes.
    void (async () => {
      const refreshRequest = refreshVariantData({
        variant_id: variantId,
        capture_snapshot_in_frontend: Boolean(preparedSnapshot),
      });

      await refreshRequest
        .unwrap()
        .then((response) => {
          const taskId = response?.task_id;

          if (taskId) {
            dispatch(
              variantApi.util.updateQueryData(
                'getRunningTasks',
                undefined,
                (tasks) => {
                  const pendingTaskIndex = tasks.findIndex(
                    (task) => task.task_id === pendingTaskId,
                  );
                  const [optimisticTask] =
                    pendingTaskIndex >= 0
                      ? tasks.splice(pendingTaskIndex, 1)
                      : [];
                  if (!tasks.some((task) => task.task_id === taskId)) {
                    tasks.unshift({
                      ...(optimisticTask ?? pendingTask),
                      task_id: taskId,
                      is_optimistic: true,
                    });
                  }
                },
              ),
            );

            if (
              response.snapshot_requested &&
              preparedSnapshot &&
              snapshotCapture
            ) {
              void snapshotCapture
                .persist(preparedSnapshot)
                .then((snapshotCreated) => {
                  if (!snapshotCreated) {
                    notification.warning({
                      message: NOTIFICATION_MESSAGES.SNAPSHOT_CREATE_FAILED,
                      description:
                        NOTIFICATION_MESSAGES.SNAPSHOT_CREATE_FAILED_DESCRIPTION,
                    });
                  }
                })
                .catch(() => {
                  notification.warning({
                    message: NOTIFICATION_MESSAGES.SNAPSHOT_CREATE_FAILED,
                    description:
                      NOTIFICATION_MESSAGES.SNAPSHOT_CREATE_FAILED_DESCRIPTION,
                  });
                });
            }
          } else {
            removePendingTask(pendingTaskId);
            notification.warning({
              message: NOTIFICATION_MESSAGES.INFO,
              description:
                response?.message ??
                NOTIFICATION_MESSAGES.REFRESH_TASK_NOT_STARTED,
            });
          }
        })
        .catch((error: AxiosBaseQueryError) => {
          removePendingTask(pendingTaskId);
          const lockDetail = (error?.data as { detail?: unknown } | undefined)
            ?.detail;
          notification.error({
            message: NOTIFICATION_MESSAGES.ERROR,
            description:
              error?.status === 423 && typeof lockDetail === 'string'
                ? lockDetail
                : NOTIFICATION_MESSAGES.REFRESH_TASK_START_FAILED,
          });
        });
    })();
  }, [
    variantId,
    hasRunningRefresh,
    dispatch,
    refreshVariantData,
    removePendingTask,
    username,
  ]);

  return {
    handleRefresh,
    isLoading,
    isPreparingSnapshot,
    hasRunningRefresh,
  };
};
